<template>
    <section class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <!-- Cabecalho no padrao das telas da area do credor/procurador -->
        <header class="bk-header">
            <span class="bk-eyebrow">
                <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
                {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
            </span>
            <h1 class="bk-title">Informação <span>bancária</span></h1>
            <p class="bk-subtitle">Conta para receber os pagamentos de cada caso. Os dados são usados pela Administração Judicial e pela devedora.</p>
        </header>

        <!-- Texto configurado pelo AJ -->
        <div v-if="dataset.description" class="bk-intro" v-html="dataset.description"></div>

        <template v-if="user">
            <div class="bk-field">
                <v-autocomplete
                    v-model="dataset.selectedClientId"
                    :items="dataset.clients"
                    item-text="name"
                    item-value="id"
                    label="Caso"
                    outlined
                    dense
                    hide-details
                    :loading="dataset.loadingClients"
                    :disabled="dataset.loading"
                    no-data-text="Nenhum caso encontrado"
                    @change="handleClientChange"
                ></v-autocomplete>
            </div>

            <div v-if="!dataset.loadingClients && !dataset.clients.length" class="bk-empty">
                <div class="bk-empty__icon"><v-icon size="34" color="darkSecondary">mdi-bank-outline</v-icon></div>
                <div class="bk-empty__title">Nenhum caso disponível</div>
                <div class="bk-empty__text">Não há casos disponíveis para cadastrar dados bancários.</div>
            </div>

            <div v-else-if="!dataset.selectedClientId" class="bk-soft">
                <v-icon small color="#7e55ab">mdi-arrow-up</v-icon> Escolha o caso acima para ver ou cadastrar os dados bancários.
            </div>

            <v-form ref="form" v-if="dataset.selectedClientId" v-model="dataForm.validForm" class="bk-form">
                <section class="bk-card">
                    <div class="bk-card__head">
                        <h2>De quem é a conta</h2>
                        <p>Se a conta for do procurador, escolha o procurador cadastrado ou cadastre um novo.</p>
                    </div>
                    <div class="bk-choice" role="radiogroup" aria-label="De quem é a conta">
                        <button type="button" role="radio" class="bk-choice__option" :class="{ 'bk-choice__option--on': dataset.accountTypeChoice === true }"
                            :aria-checked="String(dataset.accountTypeChoice === true)" @click="dataset.accountTypeChoice = true">
                            <v-icon small>mdi-account-outline</v-icon> Conta própria
                        </button>
                        <button type="button" role="radio" class="bk-choice__option" :class="{ 'bk-choice__option--on': dataset.accountTypeChoice === false }"
                            :aria-checked="String(dataset.accountTypeChoice === false)" @click="dataset.accountTypeChoice = false">
                            <v-icon small>mdi-briefcase-account-outline</v-icon> Conta do procurador
                        </button>
                    </div>

                    <div v-if="dataset.accountTypeChoice === false" class="bk-lawyer">
                        <v-select
                            v-model="dataset.data.selectedLawyer"
                            :items="dataset.lawyers.lawyer"
                            item-text="name"
                            item-value="id"
                            label="Procurador"
                            dense
                            hide-details="auto"
                            no-data-text="Nenhum procurador encontrado"
                            outlined
                            class="bk-lawyer__select"
                            @change="handleLawyerChange"
                        ></v-select>
                        <v-btn outlined color="darkSecondary" class="bk-btn" @click="openProcuratorModal">
                            <v-icon left small>mdi-plus</v-icon> Cadastrar procurador
                        </v-btn>
                    </div>
                    <div v-if="dataset.accountTypeChoice === false && (!Array.isArray(dataset.lawyers.lawyer) || !dataset.lawyers.lawyer.length)" class="bk-muted mt-2">
                        Nenhum procurador cadastrado ainda.
                    </div>
                </section>

                <section class="bk-card">
                    <div class="bk-card__head">
                        <h2>Banco e conta</h2>
                        <p>Dados da conta que vai receber os valores deste caso.</p>
                    </div>
                    <v-row dense>
                        <v-col cols="12" sm="8">
                            <v-text-field dense label="Nome do banco" :rules="[$rules.required]" v-model="dataset.data.bank_name"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field dense label="Número do banco" :rules="[$rules.required]" v-model="dataset.data.bank_code"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-text-field dense label="Agência" :rules="[$rules.required]" v-model="dataset.data.agency"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="8" sm="4">
                            <v-text-field dense label="Número da conta" :rules="[$rules.required]" v-model="dataset.data.account_number"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="4" sm="2">
                            <v-text-field dense label="Dígito" v-model="dataset.data.account_digit"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-select dense :items="dataset.accountType" label="Tipo de conta" :rules="[$rules.required]"
                                v-model="dataset.data.account_type" item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                        </v-col>
                    </v-row>
                </section>

                <section class="bk-card">
                    <div class="bk-card__head">
                        <h2>Titular e PIX</h2>
                        <p>CPF ou CNPJ do titular da conta e, se quiser, a chave PIX.</p>
                    </div>
                    <v-row dense>
                        <v-col cols="12" sm="4">
                            <v-text-field dense id="registration" label="CPF/CNPJ do titular" v-model="dataset.data.registration" required
                                v-mask="['###.###.###-##', '##.###.###/####-##']" :rules="[$rules.required, validateRegistration]"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-select dense :items="dataset.pixKeyTypes" label="Tipo de chave PIX" v-model="dataset.data.pix_type"
                                item-value="id" item-text="name" outlined hide-details="auto"></v-select>
                        </v-col>
                        <v-col cols="12" sm="5">
                            <v-text-field dense label="Chave PIX" :rules="[pixKeyValidation(dataset.data.pix_type)]" v-model="dataset.data.pix_key"
                                outlined hide-details="auto"></v-text-field>
                        </v-col>
                    </v-row>
                </section>

                <div class="bk-save">
                    <v-btn depressed color="primary" class="bk-btn bk-btn--main" :loading="dataset.loading" :disabled="dataset.loading" @click="save()">
                        Salvar dados bancários
                    </v-btn>
                </div>
            </v-form>
        </template>

        <login-required-notice v-else />

        <v-dialog v-model="procuratorModal" max-width="560px">
            <v-form ref="formNewLawyer" v-model="valid">
                <v-card class="bk-dialog">
                    <div class="bk-dialog__head">
                        <div><span class="bk-eyebrow">INFORMAÇÃO BANCÁRIA</span><h2 class="bk-dialog__title">Cadastrar procurador</h2></div>
                        <v-btn icon aria-label="Fechar" @click="closeProcuratorModal"><v-icon>mdi-close</v-icon></v-btn>
                    </div>
                    <v-card-text class="bk-dialog__body bk-form">
                        <v-row dense>
                            <v-col cols="12">
                                <v-text-field dense id="procurator_name" label="Nome do procurador" v-model="dataset.data.procurator_name" required
                                    :rules="[$rules.required]" outlined hide-details="auto"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="7">
                                <v-text-field dense id="procurator_federal_registration" label="OAB (se advogado)" v-model="dataset.data.procurator_federal_registration"
                                    :rules="dataset.data.procurator_federal_registration_uf ? [$rules.required] : []" outlined hide-details="auto"></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="5">
                                <v-autocomplete :disabled="dataset.loading" dense :items="dataset.statesList"
                                    label="UF da OAB" v-model="dataset.data.procurator_federal_registration_uf" item-value="name"
                                    item-text="name" :rules="dataset.data.procurator_federal_registration ? [$rules.required] : []" outlined hide-details="auto"></v-autocomplete>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field dense id="procurator_email" label="E-mail" v-model="dataset.data.procurator_email" required
                                    :rules="[$rules.required]" outlined hide-details="auto"></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-file-input dense accept="image/png, image/jpeg, application/pdf" prepend-icon="" prepend-inner-icon="mdi-paperclip" outlined
                                    counter required :rules="[$rules.required, $rules.fileSize(4)]" show-size
                                    hint="O arquivo não pode ser superior a 4MB" hide-details="auto"
                                    label="Documento de identificação do procurador" :clearable="true" @change="onProcuratorSelected($event)" ref="procurator_file">
                                </v-file-input>
                            </v-col>
                            <v-col cols="12">
                                <v-file-input dense accept="image/png, image/jpeg, application/pdf" prepend-icon="" prepend-inner-icon="mdi-paperclip" outlined
                                    counter required :rules="[$rules.required, $rules.fileSize(4)]" show-size
                                    hint="O arquivo não pode ser superior a 4MB" hide-details="auto"
                                    label="Procuração" :clearable="true" @change="onProcurationSelected($event)" ref="procuration_file">
                                </v-file-input>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions class="bk-dialog__footer">
                        <v-spacer></v-spacer>
                        <v-btn text class="bk-action" @click="closeProcuratorModal">Cancelar</v-btn>
                        <v-btn depressed color="primary" class="bk-btn" :loading="dataset.loading" :disabled="dataset.loading" @click="saveLawyer()">Salvar</v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>

        <v-row>
            <whatsButton/>
        </v-row>

        <v-row>
            <topButton/>
        </v-row>
    </section>
</template>

<script>
    import 'moment/locale/pt-br'
    import { eventHub } from '@/main'
    import axios from 'axios'
    import { cpf, cnpj } from 'cpf-cnpj-validator'
    import statesList from "@/common/data/states-list.data"
    import pixKeyTypes from '@/common/data/pix-key-types.data';
    
    import topButton from '@/components/layout/GoToTopButton.vue';
    import whatsButton from '@/components/layout/WhatsAppButton.vue';
  
    export default {
        name: 'BankInformation',

        computed: {
            isProcurator() {
                return !!this.user && Number(this.user.type) === 2;
            },
        },

        data: () => ({
            user: null,
            valid:null,
            procuratorModal: false,
            dataset: {
                accountType: [{ id: 1, name: "Conta corrente" }, { id: 2, name: "Conta poupança" }],
                pixKeyTypes: pixKeyTypes,
                statesList: statesList,
                data: {
                    bank_name: '',
                    bank_code: '',
                    agency: '',
                    account_number: '',
                    account_digit: '',
                    account_type: null,
                    registration: '',
                    pixKeyType: null, 
                    pix_key: '', 
                    selectedLawyer: null,
                },
                lawyers: [],
                clients: [],
                selectedClientId: null,
                loadingClients: false,
                accountTypeChoice: null,
                loading: false,
            },
            dataForm: {
                validForm: true,
                addLoading: false,
                updateLoading: false,
                removeLoading: false,
            },
           
            
        }),
        
        components: {
            topButton,
            whatsButton,
        },
    
        created() {
            if (this.$root.user) {
                this.user = this.$root.user;
                this.startLoad();
            }
                
        },
    
        methods: {
            openRegister() {
                eventHub.$emit('DIALOG-REGISTER', true)
            },

            async startLoad(){
                await this.loadText();
                await this.loadClients();

                // Vindo do botao da pagina do caso (?client_id=): ja abre com o caso selecionado.
                const requestedId = Number(this.$route.query.client_id);
                const requested = requestedId ? this.dataset.clients.find((item) => Number(item.id) === requestedId) : null;

                if (requested) {
                    this.dataset.selectedClientId = requested.id;
                    await this.handleClientChange();
                } else if (this.dataset.clients.length === 1) {
                    this.dataset.selectedClientId = this.dataset.clients[0].id;
                    await this.handleClientChange();
                }
            },

            emptyBankInformation() {
                return {
                    client_id: this.dataset.selectedClientId,
                    bank_name: '',
                    bank_code: '',
                    agency: '',
                    account_number: '',
                    account_digit: '',
                    account_type: null,
                    registration: '',
                    pix_type: null,
                    pix_key: '',
                    selectedLawyer: null,
                };
            },

            async loadClients() {
                this.dataset.loadingClients = true;
                try {
                    const result = await axios.get('/api/clients', { params: { page: 1, limit: 100000 } });
                    const clients = result.data && result.data.content && result.data.content.data
                        ? result.data.content.data
                        : [];
                    // So casos com a chave "Dados bancarios" ligada na Visao Credor.
                    this.dataset.clients = clients
                        .filter((client) => (client.portal_bank_enabled === undefined
                ? (client.creditor_portal_enabled === undefined || Number(client.creditor_portal_enabled) !== 0)
                : Number(client.portal_bank_enabled) !== 0))
                        .sort((a, b) => a.name.localeCompare(b.name));
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
                } finally {
                    this.dataset.loadingClients = false;
                }
            },

            async handleClientChange() {
                this.dataset.data = this.emptyBankInformation();
                this.dataset.accountTypeChoice = null;
                await this.loadData();
                await this.loadLawyer();
            },

            closeProcuratorModal() {
                this.procuratorModal = false;
            },

            openProcuratorModal() {
                this.procuratorModal = true;
            },

            onAccountTypeChange(value) {
                
                if (this.dataset.accountTypeChoice === value) {
                    this.dataset.accountTypeChoice = null; 
                } else {
                    this.dataset.accountTypeChoice = value; 
                }
            },

            validateRegistration(value) {
                if (!value) return true; 

                const cleanValue = value.replace(/\D/g, '');

                if (cleanValue.length <= 11) {
                    return this.$rules.cpf(value); 
                } else {
                    return this.$rules.cnpj(value);
                }
            },
            
            formatCpfCnpj(value) {
                return value.length == 14 ? cnpj.format(value) : cpf.format(value);
            },

            formatRegistration() {
                this.dataset.data.registration = this.formatCpfCnpj(this.dataset.data.registration);
            },

            handleLawyerChange(value) { 
                this.dataset.data.selectedLawyer = value
            },

            onProcurationSelected(procuration_file) {
                this.dataset.data.procuration_file = procuration_file;
            },

            onProcuratorSelected(procurator_file) {
                this.dataset.data.procurator_file = procurator_file;
            },

            pixKeyValidation(pixKeyType) {
                return value => {
                    if (pixKeyType === 1) {
                        return this.isValidCpf(value) || 'CPF inválido para chave PIX.';
                    } else if (pixKeyType === 2) {
                        return this.isValidCnpj(value) || 'CNPJ inválido para chave PIX.';
                        // } else if (pixKeyType === 3) {
                        // return this.isValidPhone(value) || 'Telefone inválido para chave PIX.';
                    } else if (pixKeyType === 4) {
                        return this.isValidEmail(value) || 'E-mail inválido para chave PIX.';
                    } else if (pixKeyType === 5) {
                        return value.length === 32 || 'Chave aleatória deve ter 32 caracteres.';
                    }
                    return true;
                };
            },

            isValidCpf(value) {
                return value.length === 11 || 'Chave CPF deve ter 11 digitos.';
            },

            isValidCnpj(value) {
                return value.length === 14 || 'Chave CNPJ deve ter 14 digitos.';
            },

            isValidEmail(value) {
                return value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'E-mail inválido.';
            },
    

            async loadText() {

                this.dataset.loading = true;

                try {
                    const result = await axios.get(`/api/dynamic-infos/keys`, {
                        params: { keys: 'bank-information-description' }
                    })
        
                    this.dataset.description = result.data.content && result.data.content.length > 0 ? result.data.content[0].value : ''; 
        
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    })
                }finally {
                    this.dataset.loading = false;
                }
            },


            async loadData() {

                if (!this.dataset.selectedClientId) return;

                this.dataset.loading = true;

                try {
                    const result = await axios.get(`/api/users/bank-information`, {
                        params: { client_id: this.dataset.selectedClientId }
                    });

                    if (result.data && result.data.content && Array.isArray(result.data.content.data) && result.data.content.data.length > 0) {
                        this.dataset.data = {
                            ...this.emptyBankInformation(),
                            ...result.data.content.data[0],
                        };

                        // const registration = this.dataset.data.registration;
                        // if (registration) {
                        //     this.dataset.data.registration = registration.length < 14 ? cpf.format(registration) : cnpj.format(registration);
                        // }

                        if (this.dataset.data.lawyer_id == null) {
                            this.dataset.accountTypeChoice = true;
                        }

                    } else {
                        this.dataset.data = this.emptyBankInformation();
                        this.dataset.accountTypeChoice = null;

                    }

                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    });
                } finally {
                    this.dataset.loading = false;
                }
            },

            async loadLawyer() {

                this.dataset.loading = true;

                try {
                    const result = await axios.get(`/api/users/lawyer`);

                    const lawyers = result.data.content.data.map(item => item.lawyer);

                    this.$set(this.dataset.lawyers, 'lawyer', lawyers);

                    if (Array.isArray(this.dataset.lawyers.lawyer) && this.dataset.lawyers.lawyer.length > 0 && this.dataset.data.lawyer_id) {
                        
                        const selectedLawyer = this.dataset.lawyers.lawyer.find(
                            lawyer => lawyer.id === this.dataset.data.lawyer_id
                        );
            
                        if (selectedLawyer) {
                            this.dataset.data.selectedLawyer = selectedLawyer.id;
                            this.dataset.accountTypeChoice = false
                        }
                    }

                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    });
                } finally {
                    this.dataset.loading = false;
                }
            },

            goBack() {
                this.dataset.data = {};
                eventHub.$emit('DIALOG-REGISTER', false)
            },

            async saveLawyer(){
            
                if (!this.$refs.formNewLawyer.validate()) return;

                const proceedAction = await this.$dialog.confirm({
                    text: 'Deseja salvar as informações do procurador?',
                    title: 'Salvar dados procurador',
                    actions: {
                        false: 'Cancelar',
                        true: 'Salvar'
                    }
                })

                if (!proceedAction) return;

                this.dataset.loading = true;
                
                const payload = this.buildPayloadProcurator();
                
                try {
                    const response = await axios.post('/api/users/lawyer', payload);

                    this.$dialog.notify.info("Informações do procurador salvas com sucesso!", { position: "top-right", timeout: 3000 });

         
                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    })
                } finally {
                    this.dataset.loading = false;
                }

                this.closeProcuratorModal();
                this.loadLawyer();
            },


            async save() {
                if (!this.dataset.selectedClientId) {
                    this.$dialog.notify.warning('Selecione o caso.', { position: 'top-right', timeout: 2000 });
                    return;
                }

                if (!this.$refs.form.validate()) return;

                const proceedAction = await this.$dialog.confirm({
                    text: 'Deseja salvar as informações bancárias?',
                    title: 'Salvar informações',
                    actions: {
                        false: 'Cancelar',
                        true: 'Salvar'
                    }
                })

                if (!proceedAction) return;

                this.dataset.loading = true;
                
                const payload = this.buildPayload();
                
                try {
                    const response = await axios.post('/api/users/bank-information', payload);

                    this.$dialog.notify.info("Informações bancárias salvas com sucesso!", { position: "top-right", timeout: 3000 });

                    await this.loadData();
                    await this.loadLawyer();

                } catch (error) {
                    this.$dialog.notify.error(this.$codes(error), {
                        position: 'top-right',
                        timeout: 2000
                    })
                } finally {
                    this.dataset.loading = false;
                }

            },


            buildPayload() {
                const formData = new FormData()

                    formData.append('client_id', this.dataset.selectedClientId);
                    
                    if (this.dataset.data.bank_name) {
                        formData.append('bank_name', this.dataset.data.bank_name);
                    }
                
                    if (this.dataset.data.registration) {
                        formData.append('registration', this.dataset.data.registration.replace(/\D/g, ''));
                    }

                    if (this.dataset.data.bank_code) {
                        formData.append('bank_code', this.dataset.data.bank_code);
                    }

                    if (this.dataset.data.agency) {
                        formData.append('agency', this.dataset.data.agency);
                    }

                    if (this.dataset.data.account_number) {
                        formData.append('account_number', this.dataset.data.account_number);
                    }

                    if (this.dataset.data.account_digit) {
                        formData.append('account_digit', this.dataset.data.account_digit);
                    }

                    if (this.dataset.data.account_type) {
                        formData.append('account_type', this.dataset.data.account_type);
                    }

                    if (this.dataset.data.pix_type) {
                        formData.append('pix_type', this.dataset.data.pix_type);
                    }

                    if (this.dataset.data.pix_key) {
                        formData.append('pix_key', this.dataset.data.pix_key);
                    }

                    if (this.dataset.data.selectedLawyer && this.dataset.accountTypeChoice === false) {
                        formData.append('lawyer_id', this.dataset.data.selectedLawyer);   
                    }

                return formData;
            },


            buildPayloadProcurator() {
                const formData = new FormData()
                    
                    if (this.dataset.data.procurator_name) {
                        formData.append('procurator_name', this.dataset.data.procurator_name);
                    }

                    if (this.dataset.data.procurator_email) {
                        formData.append('procurator_email', this.dataset.data.procurator_email);
                    }

                    formData.append('procurator_federal_registration', this.dataset.data.procurator_federal_registration || '');
                    formData.append('procurator_federal_registration_uf', this.dataset.data.procurator_federal_registration_uf || '');

                    if (this.dataset.data.procurator_file){
                    formData.append('procurator_file', this.dataset.data.procurator_file) 
                    }

                    if (this.dataset.data.procuration_file){
                    formData.append('procuration_file', this.dataset.data.procuration_file) 
                    }

                return formData;
            }
        }
    }
  
</script>
  
<style lang="scss">
.title-page {
    font-family: 'Raleway';
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}
  
.underline::after {
    content: '';
    display: block;
    margin: 5px 0;
    height: 3px;
    width: 180px;
    background: var(--v-primary-base);
}
  
.title-h1 {
    font-size: 40px;
    color: var(--v-mediumGray-base);
}
  
.text-p {
    font-size: 20px;
    font-family: 'Raleway';
}
  
.sub-title {
    color: rgb(75, 72, 72);
    font-family: 'Raleway';
    font-size: 36px;
    font-style: normal;
    font-weight: 500;
    line-height: 38px;
}

</style>

<style lang="scss" scoped>
/* Mesmo padrao visual das telas da area do credor/procurador. */
.bk-header { padding: 32px 0 24px; }
.bk-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: .12em; color: #766386; }
.bk-title { margin: 14px 0 10px; font-size: clamp(28px, 3.4vw, 40px); font-weight: 700; color: #25113c; letter-spacing: -.035em; line-height: 1.2; }
.bk-title span { color: #806697; }
.bk-subtitle { margin: 0; color: #73677c; font-size: 14px; }
.bk-muted { font-size: 13px; color: rgba(24, 0, 50, .55); }

.bk-intro { padding: 16px 20px; border-radius: 14px; background: rgba(120, 119, 229, .09); color: #2a1a40; font-size: 14px; line-height: 1.6; margin-bottom: 24px; }
.bk-intro ::v-deep p:last-child { margin-bottom: 0; }
.bk-soft { display: flex; align-items: center; gap: 10px; padding: 16px 20px; border-radius: 14px; background: rgba(120, 119, 229, .07); color: #4a3a5c; font-size: 14px; margin: 16px 0 40px; }

.bk-field { max-width: 520px; margin-bottom: 20px; }
.bk-field ::v-deep .v-text-field--outlined fieldset, .bk-form ::v-deep .v-text-field--outlined fieldset { border-color: rgba(126, 85, 171, .45); border-radius: 10px; }
.bk-field ::v-deep .v-text-field--outlined:hover fieldset, .bk-form ::v-deep .v-text-field--outlined:hover fieldset { border-color: #7e55ab; }
.bk-field ::v-deep .v-input__slot, .bk-form ::v-deep .v-input__slot { border-radius: 10px !important; }

.bk-card { padding: 22px 22px 18px; border-radius: 16px; background: #fff; border: 1px solid rgba(24, 0, 50, .07); margin-bottom: 16px; }
.bk-card__head { margin-bottom: 14px; }
.bk-card__head h2 { margin: 0; font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #7e55ab; }
.bk-card__head p { margin: 4px 0 0; font-size: 13px; color: rgba(24, 0, 50, .55); }

.bk-choice { display: inline-flex; gap: 4px; padding: 4px; border-radius: 12px; background: #f3f2fb; flex-wrap: wrap; }
.bk-choice__option { display: inline-flex; align-items: center; gap: 6px; border: 0; background: transparent; padding: 8px 16px; border-radius: 9px; font-size: 14px; font-weight: 500; color: rgba(24, 0, 50, .65); cursor: pointer; transition: background-color .15s, color .15s, box-shadow .15s; }
.bk-choice__option .v-icon { color: inherit; }
.bk-choice__option--on { background: #fff; color: #180032; font-weight: 600; box-shadow: 0 1px 3px rgba(24, 0, 50, .12); }
.bk-lawyer { display: flex; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-top: 16px; }
.bk-lawyer__select { flex: 1 1 320px; }

.bk-btn { height: 40px !important; padding: 0 18px !important; border-radius: 10px; text-transform: none; letter-spacing: 0; font-weight: 600; }
.bk-btn--main { height: 44px !important; padding: 0 24px !important; }
.bk-action { text-transform: none; letter-spacing: normal; font-weight: 600; }
.bk-save { display: flex; justify-content: flex-end; margin: 4px 0 40px; }

.bk-empty { text-align: center; padding: 40px 16px 56px; }
.bk-empty__icon { width: 72px; height: 72px; border-radius: 50%; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; background: rgba(120, 119, 229, .12); }
.bk-empty__title { font-size: 17px; font-weight: 600; color: #180032; }
.bk-empty__text { margin-top: 6px; color: rgba(24, 0, 50, .6); font-size: 14px; }

.bk-dialog { border-radius: 18px !important; }
.bk-dialog__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 22px 24px 6px; }
.bk-dialog__title { margin: 6px 0 0; font-size: 21px; font-weight: 700; color: #25113c; letter-spacing: -.02em; }
.bk-dialog__body { padding: 12px 24px 8px !important; }
.bk-dialog__footer { gap: 8px; padding: 12px 24px 20px !important; border-top: 1px solid rgba(24, 0, 50, .06); }

@media (max-width: 760px) {
    .bk-save .bk-btn--main { width: 100%; }
    .bk-field { max-width: none; }
}
</style>
